import { useEffect, useRef, useState, type FormEvent } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { logout as signOut } from '../lib/firebase';
import { useNames } from '../lib/directory';
import { Icon, useToast } from './ui';
import { chatChime } from '../lib/chat';
import { useSettings } from '../lib/settings';
import { useMe } from '../lib/me';
import { AlertsProvider, useAlerts } from '../lib/alerts';
import { RealtimeProvider, useLive, useRealtimeConnected } from '../lib/realtime';
import { can, type ChatThread } from '@torapon/shared';

const NAV = [
  { to: '/', icon: 'map', label: 'マップ', title: '配車マップ' },
  { to: '/status', icon: 'status', label: '状況', title: 'ドライバーの状況' },
  { to: '/timeline', icon: 'timeline', label: '運行表', title: '運行表' },
  { to: '/deliveries', icon: 'list', label: '配送', title: '配送の一覧' },
  { to: '/dispatch', icon: 'plus', label: '配送指示', title: '配送指示の入力' },
  { to: '/schedule', icon: 'calendar', label: '予定', title: '月間スケジュール' },
  { to: '/roll-calls', icon: 'check', label: '点呼', title: '点呼・アルコールチェックの確認' },
  { to: '/issues', icon: 'status', label: '問題報告', title: '問題報告の対応' },
  { to: '/chat', icon: 'chat', label: 'チャット', title: 'チャット' },
  { to: '/drivers', icon: 'truck', label: 'ドライバー', title: 'ドライバー' },
  { to: '/staff', icon: 'people', label: 'スタッフ', title: '事務所スタッフ', perm: 'manageStaff' as const },
  { to: '/places', icon: 'pin', label: '場所', title: 'お気に入り・ひな形の管理' },
  { to: '/clients', icon: 'people', label: '荷主', title: '荷主' },
  { to: '/vehicles', icon: 'car', label: '車両', title: '車両・検知器' },
  { to: '/records', icon: 'doc', label: '記録・印刷', title: '記録・印刷', perm: 'viewRecords' as const },
  { to: '/audit', icon: 'log', label: '操作ログ', title: '操作ログ', perm: 'viewAudit' as const },
  { to: '/system', icon: 'gear', label: 'システム', title: 'システムの状態', perm: 'viewAudit' as const },
];

export function Shell() {
  return <RealtimeProvider><AlertsProvider><ShellInner /></AlertsProvider></RealtimeProvider>;
}

function Bell() {
  const { alerts } = useAlerts();
  const n = alerts.filter((a) => a.level === 'alert').length;
  return (
    <NavLink to="/" className="btn sm" style={{ position: 'relative', textDecoration: 'none', color: '#000' }} aria-label={`警告 ${alerts.length} 件`}>
      警告 {alerts.length}{n > 0 && <em className="badge" style={{ top: -6, right: -6 }}>{n}</em>}
    </NavLink>
  );
}

function ShellInner() {
  const me = useMe();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [waiting, setWaiting] = useState(0);
  // 点呼の確認待ちの数をメニューに出す（点呼が変わったらすぐ読み直す）
  const loadWaiting = () => api.get<{ rollCalls: unknown[] }>('/v1/office/roll-calls?status=waiting').then((r) => setWaiting(r.rollCalls.length)).catch(() => {});
  useEffect(() => { void loadWaiting(); }, []);
  useLive(['rollcalls'], loadWaiting);
  // チャットの未読：増えたら音とお知らせ（どの画面でも。仕様書「事務所の受け取り」）
  const toast = useToast();
  const names = useNames();
  const namesRef = useRef(names);
  namesRef.current = names;
  const [unread, setUnread] = useState<ChatThread[]>([]);
  const prev = useRef<Map<string, number> | null>(null);
  const loadChats = () => api.get<{ threads: ChatThread[] }>('/v1/office/chats').then((r) => {
    const now = new Map(r.threads.map((x) => [x.driverId, x.unread]));
    if (prev.current) {
      const grew = r.threads.filter((x) => x.unread > (prev.current!.get(x.driverId) ?? 0));
      if (grew.length) {
        chatChime();
        for (const x of grew) toast(`${namesRef.current.driver(x.driverId)}さんからメッセージが届きました`);
      }
    }
    prev.current = now;
    setUnread(r.threads);
  }).catch(() => {});
  useEffect(() => { void loadChats(); }, []);
  useLive(['messages'], loadChats);
  const chatUnread = unread.reduce((a, x) => a + x.unread, 0);
  const { settings } = useSettings();
  const live = useRealtimeConnected();
  const title = NAV.find((n) => n.to === location.pathname)?.title ?? (location.pathname.startsWith('/drivers/') ? 'ドライバー詳細' : location.pathname === '/routines' ? '定期便の登録' : '');
  const onSearch = (e: FormEvent) => { e.preventDefault(); if (q.trim()) nav(`/deliveries?q=${encodeURIComponent(q.trim().toUpperCase())}`); setQ(''); };
  const logout = async () => { await signOut(); nav('/login'); };
  // 操作がないまま 30 分たったらログアウト（仕様書「セッション」）
  useEffect(() => {
    let last = Date.now();
    const touch = () => { last = Date.now(); };
    const evs = ['pointerdown', 'keydown', 'wheel'] as const;
    evs.forEach((e) => window.addEventListener(e, touch, { passive: true }));
    const t = setInterval(() => { if (Date.now() - last > 30 * 60_000) void logout(); }, 30_000);
    return () => { clearInterval(t); evs.forEach((e) => window.removeEventListener(e, touch)); };
  }, []);
  return (
    <div className="app">
      <nav className="rail" aria-label="メニュー">
        <div className="brand" title="とらぽん"><span style={{ color: '#fff' }}><Icon name="truck" /></span></div>
        {NAV.filter((n) => (!('perm' in n) || (me && can(me.role, n.perm!))) && (n.to !== '/chat' || settings.features.chat)).map((n) => (
          <NavLink key={n.to} to={n.to} end className={({ isActive }) => (isActive ? 'active' : '')} title={n.title}>
            <Icon name={n.icon} /><span>{n.label}</span>{n.to === '/roll-calls' && waiting > 0 && <em className="badge" aria-label={`確認待ち ${waiting} 件`}>{waiting}</em>}
            {n.to === '/chat' && chatUnread > 0 && <em className="badge" aria-label={`未読 ${chatUnread} 件`}>{chatUnread}</em>}
          </NavLink>
        ))}
      </nav>
      <main>
        <header className="topbar">
          <div className="title">{title}</div>
          <form className="search" onSubmit={onSearch} role="search"><Icon name="search" size={18} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="配送番号で検索" aria-label="配送番号で検索" /></form>
          <span className="sp" />
          <span title={live ? '即時に更新しています' : 'つなぎ直しています（1 分ごとに更新）'} aria-label={live ? '即時の更新：つながっています' : '即時の更新：つなぎ直し中'} style={{ width: 8, height: 8, borderRadius: '50%', background: live ? 'var(--go)' : '#bbb' }} />
          <Bell />
          {me && (
            <div className="user"><span className="av">{names.staff(me.id).slice(0, 1)}</span><div><b style={{ display: 'block', fontSize: 13 }}>{names.staff(me.id)}</b><small className="muted">{ROLE[me.role]}</small></div>
              <button className="btn sm" onClick={logout}>ログアウト</button></div>
          )}
        </header>
        <Outlet />
      </main>
    </div>
  );
}

export const ROLE = { admin: 'システム管理者', manager: '運行管理者', assistant: '補助者', dispatcher: '配車スタッフ' } as const;
